import type {Component} from 'grapesjs';
import type {ExtendedControlIcons} from './extendedcontrolicons';

export interface ComponentVOProps {
  type: string;
  propertyLabel: string;
  min: number;
  max: number;
  property: string;
  units: string | string[];
  defaults: string | number;
  extendedControls: boolean;
  component: Component | null;
  isResponsive: boolean;
  extendedControlIcons: ExtendedControlIcons | null;
}

export default class EditorBaseComponentVO {
  private propertyValue: string;
  private propertyLabelValue: string;
  private unitsValue: string | string[];
  private minValue: number;
  private maxValue: number;
  private defaultsValue: string | number;
  private typeValue: string;
  private extendedControlsValue: boolean;
  private componentValue: Component | null;
  private isResponsiveValue: boolean;
  private extendedControlIconsValue: ExtendedControlIcons | null;

  constructor(
    property: string,
    propertyLabel: string,
    units: string | string[],
    min: number,
    max: number,
    defaults: string | number,
    type: string,
    extendedControls = false,
    component: Component | null = null,
    isResponsive = false,
    extendedControlIcons: ExtendedControlIcons | null = null
  ) {
    this.propertyValue = property;
    this.propertyLabelValue = propertyLabel;
    this.unitsValue = units;
    this.minValue = min;
    this.maxValue = max;
    this.defaultsValue = defaults;
    this.typeValue = type;
    this.extendedControlsValue = extendedControls;
    this.componentValue = component;
    this.isResponsiveValue = isResponsive;
    this.extendedControlIconsValue = extendedControlIcons;

    if (component) {
      this.setDefault(component);
    }
  }

  get type(): string {
    return this.typeValue;
  }

  get propertyLabel(): string {
    return this.propertyLabelValue;
  }

  get extendedControlIcons(): ExtendedControlIcons | null {
    return this.extendedControlIconsValue;
  }

  get property(): string {
    return this.propertyValue;
  }

  get units(): string | string[] {
    return this.unitsValue;
  }

  get min(): number {
    return this.minValue;
  }

  set min(value: number) {
    this.minValue = value;
  }

  get max(): number {
    return this.maxValue;
  }

  get defaults(): string | number {
    return this.defaultsValue;
  }

  get extendedControls(): boolean {
    return this.extendedControlsValue;
  }

  get isResponsive(): boolean {
    return this.isResponsiveValue;
  }

  get component(): Component | null {
    return this.componentValue;
  }

  setDefault(component: Component): void {
    const styles = component.getStyle();
    const value = styles[this.propertyValue];
    const parsedValue = parseInt(value as string, 10);
    const isValidValue = typeof value !== 'undefined' && !isNaN(parsedValue);
    
    this.componentValue = component;

    if (isValidValue) {
      this.defaultsValue = styles[this.propertyValue] as string;
    }
  }

  toObject(): ComponentVOProps {
    return {
      type: this.type,
      propertyLabel: this.propertyLabel,
      min: this.min,
      max: this.max,
      property: this.property,
      units: this.units,
      defaults: this.defaults,
      extendedControls: this.extendedControls,
      component: this.component,
      isResponsive: this.isResponsive,
      extendedControlIcons: this.extendedControlIcons,
    };
  }
}